import React, { useState, useEffect } from "react";
import { getTemplates, AgentTemplate } from "../services/templateService";
import { AgentType } from "../constants/agentTypes";
import { debug } from "../utils/debugLogger";

interface TemplateReferenceProps {
  onCopyToInstructions: (templateText: string) => void;
  /** When set, templates for this agent type are shown first */
  agentType?: AgentType;
}

const TemplateReference: React.FC<TemplateReferenceProps> = ({
  onCopyToInstructions,
  agentType,
}) => {
  const templates = getTemplates(agentType);
  const [selectedTemplate, setSelectedTemplate] = useState<AgentTemplate | null>(
    templates[0] ?? null
  );

  useEffect(() => {
    const list = getTemplates(agentType);
    if (list.length > 0) {
      setSelectedTemplate(list[0]);
    }
  }, [agentType]);
  const [inserted, setInserted] = useState(false);

  const handleInsert = () => {
    if (!selectedTemplate) return;

    navigator.clipboard.writeText(selectedTemplate.content).catch((err) => {
      debug.error("Failed to copy template to clipboard:", err);
    });

    onCopyToInstructions(selectedTemplate.content);
    setInserted(true);
    setTimeout(() => setInserted(false), 2000);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">
        Agent Templates
      </h3>

      {/* Template selection cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        {templates.map((template) => (
          <button
            key={template.id}
            type="button"
            onClick={() => setSelectedTemplate(template)}
            className={`text-left p-4 rounded-lg border-2 transition-all ${
              selectedTemplate?.id === template.id
                ? "border-[#2f4941] bg-[#2f4941]/5 shadow-sm"
                : "border-gray-200 hover:border-gray-300 bg-gray-50/50 hover:bg-gray-50"
            }`}
          >
            <p className="font-medium text-gray-800">{template.name}</p>
            <p className="text-sm text-gray-600 mt-1">{template.description}</p>
          </button>
        ))}
      </div>

      {/* Preview area */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <div className="bg-surface-ui px-3 py-2 border-b border-gray-200 flex justify-between items-center">
          <span className="font-mono text-sm text-gray-600">
            {selectedTemplate?.name ?? "Select a template"}
          </span>
          <button
            type="button"
            onClick={handleInsert}
            disabled={!selectedTemplate}
            className="px-4 py-2 bg-[#2f4941] text-white text-sm font-medium rounded-lg hover:bg-[#1e3128] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {inserted ? "Inserted!" : "Insert to Instructions"}
          </button>
        </div>
        <pre className="p-4 bg-gray-50 text-sm font-mono overflow-auto max-h-96 whitespace-pre-wrap text-gray-800">
          {selectedTemplate?.content ?? "Select a template to view its content."}
        </pre>
      </div>
    </div>
  );
};

export default TemplateReference;
